<script setup lang="ts">
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { de, enGB } from 'date-fns/locale'
import '@vuepic/vue-datepicker/dist/main.css'

const dateFnsLocales = { de, en: enGB }

const props = defineProps({
    modelValue: {
        type: [String, Date],
        default: ''
    },
    datePickerConfig: {
        type: Object,
        required: false,
        default: () => ({}),
        validator(rawValue) {
            const validations = {
                name: rawValue => typeof rawValue?.name === 'string',
                uid: rawValue => typeof rawValue?.uid === 'string',
                locale: rawValue => rawValue?.locale === undefined || typeof rawValue?.locale === 'string',
                required: rawValue => rawValue?.required === undefined || typeof rawValue?.required === 'string',
                maxDate: rawValue => rawValue?.maxDate === undefined || typeof rawValue?.maxDate === 'object',
                placeholder: rawValue => rawValue?.placeholder === undefined || typeof rawValue?.placeholder === 'string',
                timePicker: rawValue => rawValue?.timePicker === undefined || typeof rawValue?.timePicker === 'boolean',
                state: rawValue => rawValue?.state === undefined || typeof rawValue?.state === 'boolean',
                startValidation: rawValue => rawValue?.startValidation === undefined || typeof rawValue?.startValidation === 'boolean',
                errorMessage: rawValue => rawValue?.errorMessage === undefined || typeof rawValue?.errorMessage === 'string',
            }

            return Object.values(validations).every(validation => validation(rawValue))
        }
    }
})

// Create a local reactive state for the selected value
const date = ref(props.modelValue)

/**
 * Get formatted date
 * @param date
 */
const getFormat = (date: Date | string) => {
    if (!date) return
    /*
       // Ensure the input is a valid Date object
       let parsedDate

       if (typeof date === 'string') {
           // Split the string into day, month, and year
           const [day, month, year] = date.split('.')

           // Create a Date object
           parsedDate = new Date(`${year}-${month}-${day}`) // YYYY-MM-DD format is ISO standard
       } else {
           parsedDate = date
       }
       */

    if (isNaN(date.getTime())) {
        // Handle invalid date (e.g., when the user types an invalid value)
        console.warn('Invalid date:', date)
        return '' // Return an empty string or a default value
    }

    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    // For the 'de' locale (German)
    if (props.datePickerConfig?.locale === 'de') {
        return props.datePickerConfig?.timePicker
            ? `${day}.${month}.${year} um ${hours}:${minutes} Uhr`
            : `${day}.${month}.${year}`
    }

    // For the 'en-GB' locale (British English)
    if (props.datePickerConfig?.locale === 'en') {
        return props.datePickerConfig?.timePicker
            ? `${month}/${day}/${year} at ${hours}:${minutes}`
            : `${month}/${day}/${year}`
    }

    // Default case if the locale doesn't match (fallback to en-GB format)
    return props.datePickerConfig?.timePicker
        ? `${day}/${month}/${year} at ${hours}:${minutes}`
        : `${day}/${month}/${year}`
}
const emit = defineEmits(['update:modelValue'])

// Emit Date value to parent component
watch(() => date.value, (value) => {
    emit('update:modelValue', value)
})

const textInputOptions = {
    format: props.datePickerConfig.timePicker ? props.datePickerConfig?.locale === 'de' ? 'dd.MM.yyyy HH:mm' : 'MM/dd/yyyy HH:mm' : props.datePickerConfig?.locale === 'de' ? 'dd.MM.yyyy' : 'MM/dd/yyyy'
}
</script>

<template>
    <div class="form-date-picker" :class="(! datePickerConfig?.state) && datePickerConfig?.startValidation ? 'form-error' : ''">
        <FormLabelStandalone>
            {{ datePickerConfig?.label }} <span v-if="datePickerConfig.required" class="asterisk-icon">*</span>
        </FormLabelStandalone>

        <ClientOnly>
            <VueDatePicker
                v-model="date"
                :uid="datePickerConfig.uid"
                :state="datePickerConfig.state"
                :locale="dateFnsLocales[datePickerConfig.locale as keyof typeof dateFnsLocales] ?? enGB"
                :required="datePickerConfig.required"
                :name="datePickerConfig.name"
                :max-date="datePickerConfig.maxDate"
                :placeholder="datePickerConfig.placeholder"
                :enable-time-picker="datePickerConfig.timePicker"
                :dark="true"
                :format="getFormat"
                auto-apply
                menu-class-name="dp-custom-menu"
                input-class-name="dp-custom-input"
                calendar-cell-class-name="dp-custom-cell"
                calendar-class-name="dp-custom-calendar"
                :text-input="textInputOptions"
            />
        </ClientOnly>

        <FormFieldMessage
            :msg-config="{
                message: datePickerConfig.errorMessage,
                error: datePickerConfig.errorMessage && ! datePickerConfig.state && datePickerConfig.startValidation
            }"
        />

        <span v-if="datePickerConfig.helperText" class="helper-text">
            {{ datePickerConfig.helperText }}
        </span>
    </div>
</template>

<style  lang="scss">
// stylelint-disable selector-class-pattern
.dp__action_button {
    white-space: nowrap;
}

.dp__theme_dark {
    --dp-border-radius: 10px;
    --dp-background-color: #142738;
    --dp-text-color: #fff;
    --dp-hover-color: #00b2fb;
    --dp-hover-text-color: #fff;
    --dp-hover-icon-color: #959595;
    --dp-primary-color: #00b2fb;
    --dp-primary-disabled-color: #61a8ea;
    --dp-primary-text-color: #fff;
    --dp-secondary-color: #a9a9a9;
    --dp-border-color: rgba($white, 0.5);
    --dp-menu-border-color: transparent;
    --dp-border-color-hover: #aaaeb7;
    --dp-border-color-focus: #aaaeb7;
    --dp-disabled-color: #737373;
    --dp-disabled-color-text: #d0d0d0;
    --dp-scroll-bar-background: #212121;
    --dp-scroll-bar-color: #484848;
    --dp-success-color: #49cb75;
    --dp-success-color-disabled: #49cb75;
    --dp-icon-color: #fff;
    --dp-danger-color: #ff6c4b;
    --dp-marker-color: #ff6c4b;
    --dp-tooltip-color: #3e3e3e;
    --dp-highlight-color: rgb(0 92 178 / 20%);
    --dp-range-between-dates-background-color: var(--dp-hover-color, #484848);
    --dp-range-between-dates-text-color: var(--dp-hover-text-color, #fff);
    --dp-range-between-border-color: var(--dp-hover-color, #fff);
}

[input-class-name="dp-custom-input"] {
    input {
        @include inputfield($padding: 8px 16px 8px 40px);

        &.dp__input_invalid {
            box-shadow: none;
            border-color: $form-input-text-border;

            .form-error & {
                background: $form-input-text-error-bg;
                border: $form-input-text-error-border 2px solid;
            }
        }

        &.dp__input_valid {
            box-shadow: none;
            border-color: $form-input-text-border;

            &.dp__input_focus {
                border-color: $form-input-text-border-active;
            }
        }
    }

}

.dp-custom-menu {
    background: #142738;
}

.dp__btn  {
    &.dp__month_year_select {
        white-space: nowrap;
    }
}
</style>
